import { useDateTime } from "../../../../localization/useDateTime"
import React from "react"
import { Grid, Skeleton, Typography } from "@mui/material"
import { LocalizedEnum, LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { Admin, isAdmin, isTenantUser, User } from "../../../../users/userTypes"
import { Box } from "@mui/system"
import { makeStyles } from "tss-react/mui"
import { tailwindPalette } from "../../../../theme/tailwindPalette"

const useStyles = makeStyles()((theme) => ({
    card: {
        position: "relative",
        border: "none",
        padding: theme.spacing(5),
        borderRadius: 12,
        borderWidth: 1,
        borderStyle: "solid",
        borderColor: "#E2E8F0",
        background: "#F8FAFC",
        boxShadow: "0px 2px 6px 0px rgba(0, 0, 0, 0.08)",
    },
    subtitle: {
        fontSize: "1.5rem",
        fontWeight: 700,
        color: tailwindPalette.slate[700],
        marginBottom: theme.spacing(2),
    },
    text: {
        fontSize: "1rem",
        color: tailwindPalette.slate[700],
    },
    skeletonText: {
        width: "10rem",
    },
    container: {
        padding: "0 3rem 3rem 3rem",
    },
}))

function SkeletonUserDetails() {
    const { cx, classes } = useStyles()
    return <Skeleton variant="text" animation="wave" className={cx(classes.text, classes.skeletonText)} />
}

type UserDetailsContentProps = {
    user: Admin | User | undefined
}

export function UserDetailsContent({ user }: UserDetailsContentProps) {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()

    return (
        <Grid container gap={4} className={classes.container}>
            <Grid container alignItems="center" direction="column" wrap="nowrap" gap="1.5rem" className={classes.card}>
                <Grid container item justifyContent="space-between">
                    <LocalizedStrict id="user-details-user-id">
                        <Typography className={classes.text} variant="body2">
                            User id:
                        </Typography>
                    </LocalizedStrict>
                    {user === undefined ? (
                        <SkeletonUserDetails />
                    ) : (
                        <Typography fontWeight={500} className={classes.text} variant="body2">
                            {isAdmin(user) ? user.user.id : user.id}
                        </Typography>
                    )}
                </Grid>
                {isTenantUser(user) && (
                    <Grid container item justifyContent="space-between">
                        <LocalizedStrict id="user-details-employee-id">
                            <Typography className={classes.text} variant="body2">
                                Employee id:
                            </Typography>
                        </LocalizedStrict>
                        <Typography fontWeight={500} className={classes.text} variant="body2">
                            {user?.employeeId ? user?.employeeId : "-"}
                        </Typography>
                    </Grid>
                )}
                {isTenantUser(user) && (
                    <Grid container item justifyContent="space-between">
                        <LocalizedStrict id="user-details-user-group">
                            <Typography className={classes.text} variant="body2">
                                User group:
                            </Typography>
                        </LocalizedStrict>
                        <Typography fontWeight={500} className={classes.text} variant="body2">
                            {user?.userGroup?.name ?? "-"}
                        </Typography>
                    </Grid>
                )}
                {isTenantUser(user) && (
                    <Grid container item justifyContent="space-between">
                        <LocalizedStrict id="user-details-department">
                            <Typography className={classes.text} variant="body2">
                                Department:
                            </Typography>
                        </LocalizedStrict>
                        <Typography fontWeight={500} className={classes.text} variant="body2">
                            {user?.department?.name ?? "-"}
                        </Typography>
                    </Grid>
                )}
                <Grid container item justifyContent="space-between">
                    <LocalizedStrict id="user-details-name">
                        <Typography className={classes.text} variant="body2">
                            Name:
                        </Typography>
                    </LocalizedStrict>
                    {user === undefined ? (
                        <SkeletonUserDetails />
                    ) : (
                        <Typography fontWeight={500} className={classes.text} variant="body2">
                            {isAdmin(user) ? user.user.displayName : user.displayName}
                        </Typography>
                    )}
                </Grid>
                {isAdmin(user) && (
                    <Grid container item justifyContent="space-between">
                        <LocalizedStrict id="user-details-role">
                            <Typography className={classes.text} variant="body2">
                                User roles:
                            </Typography>
                        </LocalizedStrict>

                        <Typography fontWeight={500} className={classes.text} variant="body2">
                            {user?.roles?.length
                                ? user?.roles?.map((role, index) => (
                                      <React.Fragment key={role}>
                                          <LocalizedEnum base="user-details-role-type" enumValue={role}>
                                              <span>{role}</span>
                                          </LocalizedEnum>
                                          {index !== user?.roles!.length - 1 && ", "}
                                      </React.Fragment>
                                  ))
                                : "-"}
                        </Typography>
                    </Grid>
                )}
                <Grid container item justifyContent="space-between">
                    <LocalizedStrict id="user-details-email">
                        <Typography className={classes.text} variant="body2">
                            Email:
                        </Typography>
                    </LocalizedStrict>
                    {user === undefined ? (
                        <SkeletonUserDetails />
                    ) : (
                        <Typography fontWeight={500} className={classes.text} variant="body2">
                            {isAdmin(user) ? user.user.email : user.email}
                        </Typography>
                    )}
                </Grid>
                <Grid container item justifyContent="space-between">
                    <LocalizedStrict id="user-details-mobile">
                        <Typography className={classes.text} variant="body2">
                            Mobile:
                        </Typography>
                    </LocalizedStrict>
                    {user === undefined ? (
                        <SkeletonUserDetails />
                    ) : (
                        <Typography fontWeight={500} className={classes.text} variant="body2">
                            {isAdmin(user) ? user.user.mobileNumber : (user.mobileNumber ?? "-")}
                        </Typography>
                    )}
                </Grid>
                {isTenantUser(user) && (
                    <Grid container item justifyContent="space-between">
                        <LocalizedStrict id="user-details-phone">
                            <Typography className={classes.text} variant="body2">
                                Phone:
                            </Typography>
                        </LocalizedStrict>
                        <Typography fontWeight={500} className={classes.text} variant="body2">
                            {user?.phoneNumber ?? "-"}
                        </Typography>
                    </Grid>
                )}
            </Grid>

            {isTenantUser(user) && (
                <Grid container alignItems="center" direction="column" wrap="nowrap">
                    <Grid alignSelf="start" item>
                        <LocalizedStrict id="user-details-workgroups-title">
                            <Typography variant="h4" className={classes.subtitle}>
                                Workgroups
                            </Typography>
                        </LocalizedStrict>
                    </Grid>
                    <Grid container item className={classes.card} gap="1rem">
                        {user.workgroups && user.workgroups.length > 0 ? (
                            user.workgroups.map((workgroup) => (
                                <Grid container item key={workgroup.id}>
                                    <Typography className={classes.text} variant="body2">
                                        {workgroup.name}
                                    </Typography>
                                </Grid>
                            ))
                        ) : (
                            <Typography className={classes.text} variant="body2">
                                -
                            </Typography>
                        )}
                    </Grid>
                </Grid>
            )}

            {isTenantUser(user) && (
                <Grid container alignItems="center" direction="column" wrap="nowrap">
                    <Grid item>
                        <LocalizedStrict id="user-details-settings-title">
                            <Typography variant="h4" className={classes.subtitle}>
                                Settings
                            </Typography>
                        </LocalizedStrict>
                    </Grid>
                    <Grid container item className={classes.card}>
                        <Grid container item justifyContent="space-between">
                            <LocalizedStrict id="user-details-sync-jobs">
                                <Typography className={classes.text} variant="body2">
                                    Disconnect sync jobs:
                                </Typography>
                            </LocalizedStrict>
                            <LocalizedStrict
                                id={
                                    user.settings?.ignoreUserSyncJobs
                                        ? "user-details-sync-jobs-ignored"
                                        : "user-details-sync-jobs-included"
                                }
                            >
                                <Typography fontWeight={500} className={classes.text} variant="body2">
                                    {user?.settings?.ignoreUserSyncJobs ? "YES" : "NO"}
                                </Typography>
                            </LocalizedStrict>
                        </Grid>
                    </Grid>
                </Grid>
            )}

            {isAdmin(user) && user.securitySettings && (
                <Grid container alignItems="center" direction="column" wrap="nowrap">
                    <Grid alignSelf="start" item>
                        <LocalizedStrict id="user-details-security-title">
                            <Typography variant="h4" className={classes.subtitle}>
                                Security
                            </Typography>
                        </LocalizedStrict>
                    </Grid>
                    <Grid container item gap="1.5rem" className={classes.card}>
                        <Grid container item justifyContent="space-between">
                            <LocalizedStrict id="user-details-security-manage-admins">
                                <Typography className={classes.text} variant="body2">
                                    Manage admins:
                                </Typography>
                            </LocalizedStrict>
                            <LocalizedStrict
                                id={
                                    user?.securitySettings?.allowToCreateAdmins
                                        ? "user-details-security-settings-yes"
                                        : "user-details-security-settings-no"
                                }
                            >
                                <Typography fontWeight={500} className={classes.text} variant="body2">
                                    {user?.securitySettings?.allowToCreateAdmins ? "YES" : "NO"}
                                </Typography>
                            </LocalizedStrict>
                        </Grid>

                        <Grid container item justifyContent="space-between">
                            <LocalizedStrict id="user-details-security-ip-address">
                                <Typography className={classes.text} variant="body2">
                                    IP address allowlist:
                                </Typography>
                            </LocalizedStrict>
                            <Typography fontWeight={500} className={classes.text} variant="body2">
                                {user?.securitySettings?.restrictAccessToIpAddresses
                                    ? user?.securitySettings?.restrictAccessToIpAddresses
                                    : "-"}
                            </Typography>
                        </Grid>
                    </Grid>
                </Grid>
            )}

            {user !== undefined && user.history && (
                <Grid container alignItems="center" direction="column" wrap="nowrap">
                    <Grid alignSelf="start" item>
                        <LocalizedStrict id="user-details-history-title">
                            <Typography variant="h4" className={classes.subtitle}>
                                History
                            </Typography>
                        </LocalizedStrict>
                    </Grid>
                    <Grid container item className={classes.card} gap="1.5rem">
                        <Grid container item justifyContent="space-between">
                            <LocalizedStrict id="user-details-history-created">
                                <Typography className={classes.text} variant="body2">
                                    Created:
                                </Typography>
                            </LocalizedStrict>
                            <Box>
                                <Typography fontWeight={500} className={classes.text} variant="body2">
                                    {dateTimeFactory.formatDateTime(
                                        dateTimeFactory.fromApi(user?.history?.created?.timestamp),
                                    )}
                                </Typography>
                                <Typography
                                    fontWeight={500}
                                    mt={1}
                                    className={classes.text}
                                    textAlign="right"
                                    variant="body2"
                                >
                                    {user?.history?.created?.author ?? "-"}
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid container item justifyContent="space-between">
                            <LocalizedStrict id="user-details-history-edited">
                                <Typography className={classes.text} variant="body2">
                                    Edited:
                                </Typography>
                            </LocalizedStrict>
                            <Box>
                                <Typography fontWeight={500} className={classes.text} variant="body2" textAlign="right">
                                    {dateTimeFactory.formatDateTime(
                                        dateTimeFactory.fromApi(user?.history?.updated?.timestamp),
                                    )}
                                </Typography>
                                <Typography
                                    fontWeight={500}
                                    mt={1}
                                    className={classes.text}
                                    variant="body2"
                                    textAlign="right"
                                >
                                    {user?.history?.updated?.author ?? "-"}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>
            )}
        </Grid>
    )
}
